import {
  ArrowRight,
  Briefcase,
  Calendar,
  CheckCircle,
  FileText,
  GitBranch,
  Globe,
  LoaderCircleIcon,
  Mail,
  MessageSquare,
  Send,
  Star,
  Users,
  Wallet,
  Zap,
  Award,
  TrendingUp,
  BarChart,
  Shield,
  Coins,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useWallet } from "../../context/WalletContext";
import { useContractServices } from "../../context/ContractServicesContext";
import { innovatorAPI, projectAPI, taskAPI } from "../../services/api";
import InnovatorForm from "./InnovatorForm";
import { toast } from "react-toastify";
import { ethers } from "ethers";
import skyLoader from "../../assets/home-assets/skyLoader.gif";
import InnovatorTaskBoard from "./InnovatorTaskBoard";
// Status badge component for tasks
const StatusBadge = ({ status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "under_review":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-purple-100 text-purple-800";
    }
  };

  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
        status
      )}`}
    >
      {status === "in_progress"
        ? "In Progress"
        : status === "under_review"
        ? "Under Review"
        : status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// Project card component
const ProjectCard = ({ project, showIU = false, showStaking = false }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-300 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ease-in-out group flex flex-col h-full">
      {/* Card Content */}
      <div className="p-6 flex-grow flex flex-col justify-between">
        <div>
          {/* Status and Domain */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {project.status && (
              <span
                className={`px-3 py-1 text-xs font-semibold rounded-full ${
                  project.status === "active"
                    ? "bg-green-100 text-green-700"
                    : project.status === "planning"
                    ? "bg-blue-100 text-blue-700"
                    : project.status === "completed"
                    ? "bg-purple-100 text-purple-700"
                    : "bg-yellow-100 text-yellow-700"
                }`}
              >
                {project.status.charAt(0).toUpperCase() +
                  project.status.slice(1)}
              </span>
            )}
            {project.primaryDomain && (
              <span className="px-3 py-1 text-xs font-semibold bg-indigo-100 text-indigo-700 rounded-full">
                {project.primaryDomain}
              </span>
            )}
          </div>

          {/* Title */}
          <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-1 group-hover:text-indigo-700 transition">
            {project.title}
          </h3>

          {/* Short Description */}
          <p className="text-gray-600 text-sm mb-5 line-clamp-2">
            {project.shortDescription || project.description}
          </p>
        </div>

        <div>
          {/* Metadata */}
          <div className="flex justify-between text-xs text-gray-500 mb-3">
            <div className="flex items-center gap-1">
              <Calendar size={14} />
              <span>{formatDate(project.createdAt)}</span>
            </div>
            <div className="flex items-center gap-1">
              <Users size={14} />
              <span>{project.contributors?.length || 0} contributors</span>
            </div>
          </div>

          {/* IU Holdings */}
          {showIU && project.iuHolding && (
            <div className="mt-3 p-3 bg-indigo-50 rounded-md">
              <div className="flex justify-between items-center text-indigo-700 text-xs font-medium">
                <span>IU Holdings:</span>
                <span className="text-sm font-bold">
                  {project.iuHolding} IUs ({project.ownershipPercentage || "0"}
                  %)
                </span>
              </div>
            </div>
          )}

          {/* Staked Amount */}
          {showStaking && project.stakedAmount && (
            <div className="mt-3 p-3 bg-purple-50 rounded-md">
              <div className="flex justify-between items-center text-purple-700 text-xs font-medium">
                <span>Staked Amount:</span>
                <span className="text-sm font-bold">
                  {project.stakedAmount} CEL
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 p-4 bg-blue-200 group-hover:bg-indigo-50 transition">
        <Link
          to={`/projects/token/${project?.projectTokenId}`}
          className="flex justify-center items-center gap-2 text-sm font-semibold text-indigo-600 group-hover:text-indigo-800 transition"
        >
          View Project <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};

// Staked project card component
const StakedProjectCard = ({ projectId, projectName, score }) => {
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-300 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ease-in-out group flex flex-col h-full">
      {/* Header with Project ID and Name */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4">
        <h3 className="text-lg font-bold truncate">
          {projectName}
        </h3>
        <div className="text-xs text-purple-100 mt-1">
          Project ID: {projectId}
        </div>
      </div>
      
      {/* Score Section */}
      <div className="p-4 flex-grow">
        <div className="mb-3">
          <div className="text-sm text-gray-500 mb-1">Your Staking Score</div>
          <div className="flex items-center">
            <Award className="h-5 w-5 text-indigo-500 mr-2" />
            <span className="text-xl font-bold text-indigo-700">{score}</span>
          </div>
        </div>
        
        {/* Info about staking score */}
        <div className="bg-indigo-50 p-3 rounded-lg text-xs text-indigo-700">
          <p>Staking score affects your influence and emission rewards for this project</p>
        </div>
      </div>
      
      {/* Footer */}
      <div className="border-t border-gray-200 p-4 bg-purple-50 hover:bg-indigo-50 transition">
        <Link
          to={`/projects/token/${projectId}`}
          className="flex justify-center items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition"
        >
          View Project <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};

// Stats card component
const StatCard = ({
  icon,
  title,
  value,
  bgColor = "bg-indigo-100",
  textColor = "text-indigo-600",
  isLoading = false,
}) => (
  <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-400">
    <div className="flex items-center">
      <div className={`p-3 rounded-full ${bgColor} ${textColor}`}>{icon}</div>
      <div className="ml-4">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-xl font-semibold text-gray-900">
          {isLoading ? <LoaderCircleIcon className="animate-spin" /> : value}
        </p>
      </div>
    </div>
  </div>
);

// Social link component
const SocialLink = ({ platform, link }) => {
  const getIcon = (platform) => {
    switch (platform) {
      case "github":
        return <GitBranch size={16} />;
      case "linkedin":
        return <Briefcase size={16} />;
      case "twitter":
        return <Globe size={16} />;
      case "discord":
        return <MessageSquare size={16} />;
      case "telegram":
        return <Send size={16} />;
      default:
        return <Globe size={16} />;
    }
  };

  if (!link) return null;

  return (
    <a
      href={link.startsWith("http") ? link : `https://${link}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 text-blue-400 font-medium  hover:text-indigo-600 transition-colors"
    >
      {getIcon(platform)}
      <span className="text-sm capitalize">{platform}</span>
    </a>
  );
};

function InnovatorProfile() {
  const { id } = useParams();
 
  const { walletAddress, isConnected } = useWallet();
  const { services, isReady } = useContractServices();
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    walletAddress: "",
    bio: "",
    roles: [],
    domains: [],
    skills: [],
    socials: {},
    profileImage: null,
  });
  const [projects, setProjects] = useState({
    created: [],
    contributed: [],
    invested: [],
    staked: [],
  });
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchingBlockchainData, setFetchingBlockchainData] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("created");
  const [activeTaskTab, setActiveTaskTab] = useState("all");
  const isProfileComplete = profile.name && profile.email;
  const fetchedRef = useRef(false);
  
  // State for blockchain metrics
  const [blockchainMetrics, setBlockchainMetrics] = useState({
    totalStaked: { value: '0', formatted: '0' },
    totalStakingScore: { value: '0', formatted: '0' },
    totalProjects: 0,
    totalEmissions: { value: '0', formatted: '0' },
    iuHoldings: []
  });
  
  const [stakedProjects, setStakedProjects] = useState([]);
  const [iuHoldings, setIuHoldings] = useState([]);
  const [totalIUHoldings, setTotalIUHoldings] = useState({ formatted: '0', raw: null });
  
  // Function to fetch blockchain metrics
  const fetchBlockchainMetrics = async (userAddress) => {
    if (!isReady || !services || !userAddress) {
      console.log("Blockchain services not ready or no wallet address");
      return;
    }
    
    try {
      setFetchingBlockchainData(true);
      
      const metrics = { ...blockchainMetrics };
      
      // Get total staked amount (userTotalStaked)
      try {
        // Get all projects where user has staked
        const projectIds = await services.projectStaking.getUserStakedProjects(userAddress);
        console.log(`Found ${projectIds.length} staked projects for ${userAddress}`);
        
        // Sum up total staked across all projects
        let totalStakedAmount = ethers.BigNumber.from('0');
        
        for (const projectId of projectIds) {
          try {
            const stakedAmount = await services.projectStaking.getUserTotalStakedAmount(projectId, userAddress);
            console.log(`Project ${projectId} staked amount: ${ethers.utils.formatEther(stakedAmount)} CEL`);
            totalStakedAmount = totalStakedAmount.add(stakedAmount);
          } catch (projectErr) {
            console.error(`Error getting staked amount for project ${projectId}:`, projectErr);
          }
        }
        
        metrics.totalStaked = {
          value: totalStakedAmount.toString(),
          formatted: ethers.utils.formatEther(totalStakedAmount)
        };
        console.log(`Total staked amount across all projects: ${metrics.totalStaked.formatted} CEL`);
      } catch (error) {
        console.error("Error fetching total staked amount:", error);
      }
      
      // Get total staking score (getUserTotalScore)
      try {
        const totalScore = await services.projectStaking.getUserTotalScore(userAddress);
        metrics.totalStakingScore = totalScore;
        console.log(`Total staking score: ${metrics.totalStakingScore.formatted}`);
      } catch (error) {
        console.error("Error fetching total staking score:", error);
      }
      
      // Get staked projects and their scores
      try {
        const projectIds = await services.projectStaking.getUserStakedProjects(userAddress);
        metrics.totalProjects = projectIds.length;
        console.log(`Found ${projectIds.length} staked projects`);
        
        // Get details for each staked project
        const projectsWithScores = await Promise.all(
          projectIds.map(async (projectId) => {
            try {
              // Get project staking score for user
              const score = await services.projectStaking.getUserProjectScore(projectId, userAddress);
              
              // Get project name
              let projectName = `Project #${projectId}`;
              try {
                projectName = await services.innovationUnits.getProjectName(projectId);
              } catch (nameError) {
                console.log(`Could not get project name for project ${projectId}:`, nameError);
              }
              
              return {
                projectId,
                projectName,
                score: score.formatted,
                rawScore: score.raw
              };
            } catch (error) {
              console.error(`Error getting details for staked project ${projectId}:`, error);
              return {
                projectId,
                projectName: `Project #${projectId}`,
                score: '0',
                rawScore: 0
              };
            }
          })
        );
        
        setStakedProjects(projectsWithScores);
      } catch (error) {
        console.error("Error fetching staked projects:", error);
      }
      
      // Get IU holdings with proper role identification
      try {
        // Get all IU holdings with role identification
        const holdings = await services.innovationUnits.getAllUserIUHoldings(userAddress);
        
        // Log the holdings with roles for debugging
        console.log(`Found ${holdings.length} projects with IU holdings`, holdings);
        
        // Set the state with the role-identified holdings
        setIuHoldings(holdings);
      } catch (error) {
        console.error("Error fetching IU holdings:", error);
      }
      
      // Get the total IU holdings across all projects
      try {
        const totalIUs = await services.innovationUnits.getInnovatorTotalIUs(userAddress);
        console.log(`Total IU holdings for ${userAddress}: ${totalIUs.formatted} IUs`);
        setTotalIUHoldings(totalIUs);
      } catch (error) {
        console.error("Error fetching total IU holdings:", error);
      }
      
      setBlockchainMetrics(metrics);
    } catch (error) {
      console.error("Error fetching blockchain metrics:", error);
      toast.error("Failed to fetch blockchain data");
    } finally {
      setFetchingBlockchainData(false);
    }
  };

  useEffect(() => {
    const fetchInnovatorData = async () => {
      if (fetchedRef.current) return;
      fetchedRef.current = true;

      try {
        setLoading(true);
        const profileResponse =
          !id && isConnected
            ? await innovatorAPI.getByWalletAddress(walletAddress)
            : await innovatorAPI.getByWalletAddress(id);

        if (profileResponse.data) {
          setProfile(profileResponse.data);

          // Use Promise.allSettled instead of Promise.all to handle potential API failures
          const fetchPromises = [
            projectAPI.getByCreator(profileResponse.data.walletAddress)
              .catch(err => {
                console.error("Error fetching created projects:", err);
                return { data: [] };
              }),
            projectAPI.getByContributor(profileResponse.data.walletAddress)
              .catch(err => {
                console.error("Error fetching contributed projects:", err);
                return { data: [] };
              }),
            projectAPI.getByInvestor(profileResponse.data.walletAddress)
              .catch(err => {
                console.error("Error fetching invested projects:", err);
                return { data: [] };
              }),
            typeof projectAPI.getByStaker === "function"
              ? projectAPI.getByStaker(profileResponse.data.walletAddress)
                  .catch(err => {
                    console.error("Error fetching staked projects:", err);
                    return { data: [] };
                  })
              : Promise.resolve({ data: [] }),
            taskAPI.getByInnovator(profileResponse.data.walletAddress)
              .catch(err => {
                console.error("Error fetching tasks:", err);
                return { data: [] };
              }),
          ];

          const [
            createdProjects,
            contributedProjects,
            investedProjects,
            stakedProjects,
            completedTasks,
          ] = await Promise.all(fetchPromises);

          setProjects({
            created: createdProjects.data || [],
            contributed: contributedProjects.data || [],
            invested: investedProjects.data || [],
            staked: stakedProjects.data || [],
          });

          console.log("Created projects:", createdProjects.data);
          console.log("Invested projects:", investedProjects.data);

          setTasks(completedTasks.data || []);
          
          // Fetch blockchain metrics once we have the profile
          if (isReady && services) {
            fetchBlockchainMetrics(profileResponse.data.walletAddress);
          }
        }
      } catch (err) {
        console.error("Error loading profile:", err);
        toast.error(`Something went wrong: ${err.message}`);
        setError(err.message || "Error loading profile");
      } finally {
        setLoading(false);
      }
    };

    if (id || walletAddress) {
      fetchInnovatorData();
    }
  }, [id, walletAddress, isConnected]);
  
  // Add a separate effect to fetch blockchain metrics when services become ready
  useEffect(() => {
    if (isReady && services && profile.walletAddress && !fetchingBlockchainData) {
      fetchBlockchainMetrics(profile.walletAddress);
    }
  }, [isReady, services, profile.walletAddress]);

  const handleProfileUpdate = async (updatedData) => {
    try {
      await innovatorAPI.update(profile.walletAddress, updatedData);
      // Refresh the profile data
      const updatedProfile = await innovatorAPI.getByWalletAddress(
        profile.walletAddress
      );
      setProfile(updatedProfile.data);
      setIsEditing(false);
      // Reload the page after successful update
      window.location.reload();
    } catch (error) {
      toast.error(`Something went wrong: ${error.message}`);
    }
  };

  const getTasksByStatus = (status = null) => {
    if (!status || status === "all") return tasks;
    return tasks.filter((task) => task.status === status);
  };

  const getTotalStaked = () => {
    if (!projects.staked) return 0;
    return projects.staked.reduce(
      (sum, project) => sum + (project.stakedAmount || 0),
      0
    );
  };

  const getTotalCompletedTasks = () => {
    return tasks.filter((task) => task.status === "completed").length;
  };

  const getTotalIUHoldings = () => {
    return totalIUHoldings.formatted || '0';
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen ">
          <img 
            src={skyLoader} 
            alt="Loading..." 
            className="w-100 h-100 object-contain"
          />
        </div>
    );

  // As we are auto craeting profile whil new wallet is connected, we don't need to show this form
  // if (!profile.walletAddress && isConnected) {
  //   return (
  //     <div className="min-h-screen bg-gray-50 py-8">
  //       <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
  //         <div className="bg-white rounded-lg shadow-md p-6">
  //           <h2 className="text-2xl font-bold text-gray-900 mb-4">
  //             Create Your Profile
  //           </h2>
  //           <p className="text-gray-600 mb-6">
  //             Create your profile to start contributing and creating projects.
  //           </p>
  //           <InnovatorForm
  //             onSubmitSuccess={async (data) => {
  //               try {
  //                 // Make API call to create new profile
  //                 const response = await innovatorAPI.create({
  //                   ...data,
  //                   walletAddress: walletAddress,
  //                 });
  //                 setProfile(response.data);
  //               } catch (error) {
  //                 console.error("Error creating profile:", error);
  //                 // Handle error (e.g., show error message to user)
  //               }
  //             }}
  //             connectedWallet={walletAddress}
  //             onClose={() => {}}
  //           />
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  if (!isProfileComplete) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Complete Your Profile
            </h2>
            <p className="text-gray-600 mb-6">
              Please complete your profile to access all features.
            </p>
            <InnovatorForm
              initialData={profile}
              onSubmitSuccess={(data) => setProfile(data)}
              connectedWallet={walletAddress}
              onClose={() => {}}
              isUpdate={true}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-indigo-100 p-6">
      <div className="px-4 sm:px-6 lg:px-8">
        {/* Hero Section with Gradient Background */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-400 text-white rounded-xl overflow-hidden shadow-lg mb-8">
          <div className="px-4 py-8 md:py-10">
            <div className="flex flex-col md:flex-row items-center md:items-center gap-6">
              {/* Profile Image */}
              <div className="relative flex-shrink-0">
                <div className="h-20 w-20 md:h-24 md:w-24 rounded-full ring-4 ring-white/20 overflow-hidden bg-white/10 backdrop-blur-sm shadow">
                  {profile.profileImage ? (
                    <img
                      src={profile.profileImage}
                      alt={profile.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-3xl md:text-4xl font-semibold">
                      {profile.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                  )}
                </div>

                {/* Verification Badge */}
                {profile.isVerified && (
                  <div className="absolute bottom-0 right-0 bg-green-500 p-0.5 rounded-full border-2 border-white shadow-md">
                    <CheckCircle size={14} className="text-white" />
                  </div>
                )}
              </div>

              {/* Profile Info */}
              <div className="text-center md:text-left flex-1 space-y-2">
                <h1 className="text-xl md:text-2xl font-bold">
                  {profile.name}
                </h1>

                {profile.roles && profile.roles.length > 0 && (
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    {profile.roles.map((role, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-0.5 bg-white/20 backdrop-blur-md rounded-full text-xs uppercase font-semibold tracking-wide"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                )}

                <p className="text-white/80 text-sm leading-relaxed max-w-xl mx-auto md:mx-0">
                  {profile.bio || "No bio provided"}
                </p>

                {/* Contact Info */}
                <div className="flex flex-wrap items-center gap-4 justify-center md:justify-start text-white/80 text-xs mt-2">
                  {/* {profile.email && (
                    <div className="flex items-center gap-1">
                      <Mail size={14} />
                      <span>{profile.email}</span>
                    </div>
                  )} */}

                  {profile.walletAddress && (
                    <div className="flex items-center gap-1">
                      <Wallet size={14} />
                      <span className="truncate max-w-[140px]">
                        {profile.walletAddress}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Edit Button */}
              {walletAddress?.toLowerCase() ===
                profile.walletAddress?.toLowerCase() && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="mt-4 md:mt-0 px-3 py-1 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/30 rounded-md text-sm font-semibold transition-all hover:scale-105"
                >
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-8">
          <StatCard
            icon={<Wallet size={24} />}
            title="Total CEL Staked"
            value={`${blockchainMetrics.totalStaked?.formatted || '0'} CEL`}
            bgColor="bg-purple-100"
            textColor="text-purple-600"
            isLoading={fetchingBlockchainData}
          />
          <StatCard
            icon={<Award size={24} />}
            title="Total Staking Score"
            value={blockchainMetrics.totalStakingScore?.formatted ? parseFloat(blockchainMetrics.totalStakingScore.formatted).toFixed(2) : '0.00'}
            bgColor="bg-indigo-100"
            textColor="text-indigo-600"
            isLoading={fetchingBlockchainData}
          />
          <StatCard
            icon={<Coins size={24} />}
            title="Total IU Holdings"
            value={`${totalIUHoldings.formatted || '0'} IUs`}
            bgColor="bg-blue-100"
            textColor="text-blue-600"
            isLoading={fetchingBlockchainData}
          />
          <StatCard
            icon={<Star size={24} />}
            title="Projects Involved"
            value={
              projects.created.length +
              projects.contributed.length +
              projects.invested.length +
              stakedProjects.length
            }
            bgColor="bg-amber-100"
            textColor="text-amber-600"
            isLoading={loading || fetchingBlockchainData}
          />
        </div>

        {/* About & Social */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-4">
          <div className="lg:col-span-2 border border-gray-400 rounded-xl h-full">
            <div className="bg-white rounded-xl shadow-sm p-6 h-full border border-gray-100">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                About
              </h2>
              <div className="space-y-6">
                {/* Skills */}
                {profile.skills && profile.skills.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">
                      Skills
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.skills.map((skill, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-green-400 font-medium text-gray-700 rounded-full text-sm"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Domains */}
                {profile.domains && profile.domains.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">
                      Interest Domains
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {profile.domains.map((domain, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-yellow-400 text-black font-medium rounded-full text-sm"
                        >
                          {domain}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {/* Education */}
                {profile.education && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">
                      Education
                    </h3>
                    <p className="text-gray-700">{profile.education}</p>
                  </div>
                )}
                {/* Experience */}
                {profile.experience?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-2">
                      Experience
                    </h3>
                    <div className="space-y-4">
                      {profile.experience.map((exp, index) => (
                        <div
                          key={index}
                          className="border-l-2 border-indigo-200 pl-4"
                        >
                          <h4 className="font-medium text-gray-900">
                            {exp.title}
                          </h4>
                          <p className="text-sm text-gray-600">{exp.company}</p>
                          <p className="text-sm text-gray-500">
                            {exp.duration}
                          </p>
                          <p className="mt-1 text-gray-700">
                            {exp.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="border border-gray-400 rounded-xl h-full">
            <div className="bg-white rounded-xl p-6 mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Connect
              </h2>
              <div className="space-y-4">
                {/* Social Links */}
                {profile.socials && Object.keys(profile.socials).length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-3">
                      Social Profiles
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {Object.entries(profile.socials).map(
                        ([platform, link]) => (
                          <SocialLink
                            key={platform}
                            platform={platform}
                            link={link}
                          />
                        )
                      )}
                    </div>
                  </div>
                )}
                {/* Portfolio Links */}
                {((profile.portfolio && profile.portfolio.length > 0) ||
                  (profile.portfolioLinks &&
                    profile.portfolioLinks.length > 0)) && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium text-gray-500 mb-3">
                      Portfolio
                    </h3>
                    <div className="space-y-3">
                      {profile.portfolio && profile.portfolio.length > 0
                        ? profile.portfolio.map((item, index) => (
                            <a
                              key={index}
                              href={
                                item.link.startsWith("http")
                                  ? item.link
                                  : `https://${item.link}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-indigo-600 hover:text-indigo-800"
                            >
                              <Globe size={16} className="mr-2" />
                              <span className="text-sm truncate">
                                {item.title !== "Portfolio Link"
                                  ? item.title
                                  : item.link.replace(/^https?:\/\//, "")}
                              </span>
                            </a>
                          ))
                        : profile.portfolioLinks &&
                          profile.portfolioLinks.map((link, index) => (
                            <a
                              key={index}
                              href={
                                link.startsWith("http")
                                  ? link
                                  : `https://${link}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center text-indigo-600 hover:text-indigo-800"
                            >
                              <Globe size={16} className="mr-2" />
                              <span className="text-sm truncate">
                                {link.replace(/^https?:\/\//, "")}
                              </span>
                            </a>
                          ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Projects Section */}
        <div className="mb-8 border border-gray-300 rounded-xl p-4 sm:p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-3 sm:space-y-0">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
              Projects
            </h2>

            {walletAddress?.toLowerCase() ===
              profile.walletAddress?.toLowerCase() && (
              <Link
                to="/projects/create"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
              >
                Create Project
              </Link>
            )}
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="flex flex-wrap justify-center gap-4 bg-gray-100 rounded-lg p-2 shadow-sm">
              {[
                { 
                  key: "created", 
                  label: "Created Projects", 
                  count: iuHoldings ? iuHoldings.filter(h => h.role === "creator").length : 0 
                },
                { 
                  key: "contributed", 
                  label: "Contributing", 
                  count: projects.contributed.length + (iuHoldings ? iuHoldings.filter(h => h.role === "contributor").length : 0) 
                },
                { 
                  key: "invested", 
                  label: "Invested", 
                  count: projects.invested.length + (iuHoldings ? iuHoldings.filter(h => h.role === "investor").length : 0) 
                },
                { 
                  key: "staked", 
                  label: "Staked", 
                  count: fetchingBlockchainData ? null : stakedProjects.length 
                },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`md:px-4 px-2 py-2 rounded-full text-xs md:text-sm font-semibold transition-all duration-300 transform ${
                    activeTab === tab.key
                      ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg scale-105"
                      : "bg-white text-gray-600 hover:bg-gray-50 hover:text-indigo-600 hover:scale-105"
                  }`}
                >
                  {tab.label} ({tab.count === null ? "..." : tab.count})
                </button>
              ))}
            </nav>
          </div>

          {/* Project Grid or Empty State */}
          {activeTab === "staked" && stakedProjects?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {stakedProjects.map((project) => (
                <StakedProjectCard
                  key={project.projectId}
                  projectId={project.projectId}
                  projectName={project.projectName}
                  score={project.score}
                />
              ))}
            </div>
          ) : activeTab === "staked" && stakedProjects?.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-10 text-center border border-gray-100">
              <div className="flex justify-center mb-4 text-gray-400">
                <FileText size={48} />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No staked projects yet
              </h3>
              <p className="text-gray-600 max-w-md mx-auto text-sm">
                This innovator hasn't staked in any projects yet.
              </p>
            </div>
          ) : activeTab === "created" && iuHoldings.filter(h => h.role === "creator").length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {iuHoldings
                .filter(h => h.role === "creator")
                .map((holding) => (
                  <div key={holding.projectId} className="bg-white rounded-2xl overflow-hidden border border-gray-300 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ease-in-out group flex flex-col h-full">
                    <div className="p-6 flex-grow flex flex-col justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                        </div>
                        <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-1 group-hover:text-indigo-700 transition">
                          {holding.projectName}
                        </h3>
                        <p className="text-gray-600 text-sm mb-5 line-clamp-2">
                          Project ID: {holding.projectId}
                        </p>
                      </div>
                      <div>
                        <div className="mt-3 p-3 bg-indigo-50 rounded-md">
                          <div className="flex justify-between items-center text-indigo-700 text-xs font-medium">
                            <span>IU Holdings:</span>
                            <span className="text-sm font-bold">
                              {holding.formattedBalance} IUs ({holding.percentage}%)
                            </span>
                          </div>
                        </div>
                        
                      </div>
                    </div>
                    <div className="border-t border-gray-200 p-4 bg-blue-200 group-hover:bg-indigo-50 transition">
                      <Link
                        to={`/projects/token/${holding.projectId}`}
                        className="flex justify-center items-center gap-2 text-sm font-semibold text-indigo-600 group-hover:text-indigo-800 transition"
                      >
                        View Project <ArrowRight size={16} />
                      </Link>
                    </div>
                  </div>
                ))}
            </div>
          ) : activeTab === "created" && iuHoldings.filter(h => h.role === "creator").length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm p-10 text-center border border-gray-100">
              <div className="flex justify-center mb-4 text-gray-400">
                <FileText size={48} />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No created projects yet
              </h3>
              <p className="text-gray-600 max-w-md mx-auto text-sm">
                This innovator hasn't created any projects yet.
              </p>
            </div>
          ) : activeTab === "contributed" && (projects.contributed.length > 0 || iuHoldings.filter(h => h.role === "contributor").length > 0) ? (
            <div>
              {iuHoldings.filter(h => h.role === "contributor").length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {iuHoldings
                    .filter(h => h.role === "contributor")
                    .map((holding) => (
                      <div key={holding.projectId} className="bg-white rounded-2xl overflow-hidden border border-gray-300 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ease-in-out group flex flex-col h-full">
                        <div className="p-6 flex-grow flex flex-col justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2 mb-4">
                              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                                Contributor
                              </span>
                              
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-1 group-hover:text-indigo-700 transition">
                              {holding.projectName}
                            </h3>
                            <p className="text-gray-600 text-sm mb-5 line-clamp-2">
                              Project ID: {holding.projectId}
                            </p>
                          </div>
                          <div>
                            <div className="mt-3 p-3 bg-indigo-50 rounded-md">
                              <div className="flex justify-between items-center text-indigo-700 text-xs font-medium">
                                <span>IU Holdings:</span>
                                <span className="text-sm font-bold">
                                  {holding.formattedBalance} IUs ({holding.percentage}%)
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="border-t border-gray-200 p-4 bg-blue-200 group-hover:bg-indigo-50 transition">
                          <Link
                            to={`/projects/token/${holding.projectId}`}
                            className="flex justify-center items-center gap-2 text-sm font-semibold text-indigo-600 group-hover:text-indigo-800 transition"
                          >
                            View Project <ArrowRight size={16} />
                          </Link>
                        </div>
                      </div>
                    ))}
                </div>
              )}
              
              {iuHoldings.filter(h => h.role === "contributor").length === 0 && (
                <div className="bg-white rounded-xl shadow-sm p-10 text-center border border-gray-100">
                  <div className="flex justify-center mb-4 text-gray-400">
                    <FileText size={48} />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No contributed projects yet
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto text-sm">
                    This innovator isn't contributing to any projects yet.
                  </p>
                </div>
              )}
            </div>
          ) : activeTab === "invested" && (projects.invested.length > 0 || iuHoldings.filter(h => h.role === "investor").length > 0) ? (
            <div>
              {iuHoldings.filter(h => h.role === "investor").length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {iuHoldings
                    .filter(h => h.role === "investor")
                    .map((holding) => (
                      <div key={holding.projectId} className="bg-white rounded-2xl overflow-hidden border border-gray-300 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ease-in-out group flex flex-col h-full">
                        <div className="p-6 flex-grow flex flex-col justify-between">
                          <div>
                            <div className="flex flex-wrap items-center gap-2 mb-4">
                              <span className="px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-700">
                                Investor
                              </span>
                              
                            </div>
                            <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-1 group-hover:text-indigo-700 transition">
                              {holding.projectName}
                            </h3>
                            <p className="text-gray-600 text-sm mb-5 line-clamp-2">
                              Project ID: {holding.projectId}
                            </p>
                          </div>
                          <div>
                            <div className="mt-3 p-3 bg-indigo-50 rounded-md">
                              <div className="flex justify-between items-center text-indigo-700 text-xs font-medium">
                                <span>IU Holdings:</span>
                                <span className="text-sm font-bold">
                                  {holding.formattedBalance} IUs ({holding.percentage}%)
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="border-t border-gray-200 p-4 bg-blue-200 group-hover:bg-indigo-50 transition">
                          <Link
                            to={`/projects/token/${holding.projectId}`}
                            className="flex justify-center items-center gap-2 text-sm font-semibold text-indigo-600 group-hover:text-indigo-800 transition"
                          >
                            View Project <ArrowRight size={16} />
                          </Link>
                        </div>
                      </div>
                    ))}
                </div>
              )}
              
              {iuHoldings.filter(h => h.role === "investor").length === 0 && (
                <div className="bg-white rounded-xl shadow-sm p-10 text-center border border-gray-100">
                  <div className="flex justify-center mb-4 text-gray-400">
                    <FileText size={48} />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No invested projects yet
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto text-sm">
                    This innovator hasn't invested in any projects yet.
                  </p>
                </div>
              )}
            </div>
          ) : projects[activeTab]?.length === 0 && 
             (activeTab === "created" && iuHoldings.filter(h => h.role === "creator").length === 0 ||
              activeTab === "contributed" && iuHoldings.filter(h => h.role === "contributor").length === 0 ||
              activeTab === "invested" && iuHoldings.filter(h => h.role === "investor").length === 0) ? (
            <div className="bg-white rounded-xl shadow-sm p-10 text-center border border-gray-100">
              <div className="flex justify-center mb-4 text-gray-400">
                <FileText size={48} />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No {activeTab} projects yet
              </h3>
              <p className="text-gray-600 max-w-md mx-auto text-sm">
                {activeTab === "created"
                  ? "This innovator hasn't created any projects yet."
                  : activeTab === "contributed"
                  ? "This innovator isn't contributing to any projects yet."
                  : activeTab === "invested"
                  ? "This innovator hasn't invested in any projects yet."
                  : "This innovator hasn't staked in any projects yet."}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-10 text-center border border-gray-100">
              <div className="flex justify-center mb-4 text-gray-400">
                <FileText size={48} />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No projects found
              </h3>
              <p className="text-gray-600 max-w-md mx-auto text-sm">
                There are no projects to display in this category.
              </p>
            </div>
          )}
        </div>

        {/* Tasks Section */}
        <div className="border border-gray-400 rounded-xl p-6 bg-white">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Task Board</h2>
          </div>
          <InnovatorTaskBoard walletOverride={id?.toLowerCase()} />
        </div>
      </div>
        


      {/* Edit Profile Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Edit Profile</h2>
              <button
                onClick={() => setIsEditing(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <InnovatorForm
              initialData={profile}
              onSubmitSuccess={handleProfileUpdate}
              connectedWallet={walletAddress}
              onClose={() => setIsEditing(false)}
              isUpdate={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default InnovatorProfile;
